import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { useAuth } from '@/app/contexts/AuthContext';
import { weaponCatalogSeed } from '@/data/weaponCatalog';
import { getDashboardSnapshot } from '@/features/dashboard/selectors';
import { getCurrentMasses, getEventDetail, getSignupHistoryForProfile } from '@/features/events/selectors';
import { emptyGuildState, fetchGuildState } from '@/lib/repositories/guildRepository';
import {
  deleteWeaponCatalogItem,
  saveWeaponCatalogItem,
  setGuildMembershipActive as setGuildMembershipActiveRecord,
  syncBundledWeaponCatalog,
  updateProfileAccess,
  type ProfileAccessPayload,
  type WeaponCatalogDeleteResult,
  type WeaponCatalogPayload,
} from '@/lib/repositories/adminRepository';
import {
  createEventFromTemplateQuick,
  saveEventBuilderDraft,
  type CreateEventFromTemplateQuickPayload,
} from '@/lib/repositories/eventBuilderRepository';
import { saveTemplateBuilderDraft } from '@/lib/repositories/templateRepository';
import { supabase } from '@/lib/supabase/client';
import {
  archiveEventRpc,
  cancelSignupRpc,
  deleteEventPermanentlyRpc,
  joinSlotRpc,
  refreshEventAttendanceRecordsRpc,
  staffMoveSignupRpc,
  staffRemoveSignupRpc,
} from '@/lib/supabase/rpc';
import type { ActionNotice, GuildState } from '@/types/app-state';
import type {
  DashboardSnapshot,
  EventBuilderDraft,
  EventDetailView,
  EventListItem,
  SignupHistoryGroup,
  TemplateBuilderDraft,
} from '@/types/domain';

interface GuildDataContextValue {
  state: GuildState;
  notice: ActionNotice | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  currentMasses: EventListItem[];
  dashboardSnapshot: DashboardSnapshot;
  getEventDetailView: (eventId: string) => EventDetailView | undefined;
  getMySignups: () => SignupHistoryGroup[];
  joinSlot: (eventId: string, slotId: string) => Promise<void>;
  cancelSignup: (eventId: string) => Promise<void>;
  staffMoveSignup: (signupId: string, targetEventSlotId: string) => Promise<void>;
  staffRemoveSignup: (signupId: string) => Promise<void>;
  saveProfileAccess: (payload: ProfileAccessPayload) => Promise<void>;
  setGuildMembershipActive: (profileId: string, isActive: boolean) => Promise<void>;
  saveWeapon: (payload: WeaponCatalogPayload) => Promise<void>;
  deleteWeapon: (weaponId: string, confirmationText: string) => Promise<WeaponCatalogDeleteResult | undefined>;
  syncWeaponCatalog: () => Promise<void>;
  bundledWeaponCatalogCount: number;
  saveEventBuilder: (draft: EventBuilderDraft) => Promise<string | undefined>;
  saveTemplateBuilder: (draft: TemplateBuilderDraft) => Promise<string | undefined>;
  quickCreateEventFromTemplate: (payload: CreateEventFromTemplateQuickPayload) => Promise<string | undefined>;
  archiveEvent: (eventId: string, force?: boolean) => Promise<void>;
  deleteEventPermanently: (eventId: string, confirmationText: string) => Promise<boolean>;
  refreshData: () => Promise<void>;
  clearNotice: () => void;
}

const GuildDataContext = createContext<GuildDataContextValue | null>(null);

interface LoadStateOptions {
  preserveState?: boolean;
  silent?: boolean;
  refreshAttendance?: boolean;
  attendanceEventId?: string;
}

export function GuildDataProvider({ children }: PropsWithChildren): JSX.Element {
  const { authMode, profile, isApproved, needsOnboarding, refreshProfile } = useAuth();
  const [state, setState] = useState<GuildState>(emptyGuildState);
  const [notice, setNotice] = useState<ActionNotice | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(authMode === 'supabase');
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const latestLoadRequestIdRef = useRef(0);
  const suppressRealtimeRefreshUntilRef = useRef(0);

  const currentMasses = useMemo(() => getCurrentMasses(state, profile?.id), [profile?.id, state]);
  const dashboardSnapshot = useMemo(() => getDashboardSnapshot(state), [state]);

  const startManagedRefreshWindow = useCallback((durationMs = 2000) => {
    suppressRealtimeRefreshUntilRef.current = Date.now() + durationMs;
  }, []);

  const loadState = useCallback(
    async ({
      preserveState = false,
      silent = false,
      refreshAttendance = false,
      attendanceEventId,
    }: LoadStateOptions = {}) => {
      const requestId = ++latestLoadRequestIdRef.current;

      if (!profile) {
        setState(emptyGuildState);
        setError(null);
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      if (!isApproved || needsOnboarding) {
        setState(emptyGuildState);
        setError(null);
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      if (authMode === 'mock') {
        const { createDemoState } = await import('@/data/demoData');
        setState(createDemoState());
        setError(null);
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      setError(null);

      if (preserveState && !silent) {
        setIsRefreshing(true);
      } else if (!preserveState) {
        setIsLoading(true);
      }

      try {
        if (refreshAttendance) {
          const { error: attendanceRefreshError } = await refreshEventAttendanceRecordsRpc(attendanceEventId);
          const attendanceRefreshMessage = attendanceRefreshError?.message ?? '';
          const canIgnoreAttendanceRefreshError =
            Boolean(attendanceRefreshError) &&
            /Could not find the function|refresh_event_attendance_records/i.test(attendanceRefreshMessage);

          if (attendanceRefreshError && !canIgnoreAttendanceRefreshError) {
            throw new Error(attendanceRefreshMessage);
          }
        }

        const nextState = await fetchGuildState();

        if (requestId !== latestLoadRequestIdRef.current) {
          return;
        }

        setState(nextState);
        setError(null);
      } catch (nextError) {
        if (requestId !== latestLoadRequestIdRef.current) {
          return;
        }

        const message = nextError instanceof Error ? nextError.message : 'Failed to load guild data.';
        setError(message);
      } finally {
        if (requestId !== latestLoadRequestIdRef.current) {
          return;
        }

        setIsLoading(false);

        if (!silent) {
          setIsRefreshing(false);
        }
      }
    },
    [authMode, isApproved, needsOnboarding, profile],
  );

  useEffect(() => {
    void loadState();
  }, [loadState]);

  useEffect(() => {
    if (authMode !== 'supabase' || !supabase || !profile || !isApproved || needsOnboarding) {
      return undefined;
    }

    const client = supabase;
    let refreshTimeout: number | undefined;
    let refreshNeedsAttendance = false;

    const scheduleRefresh = (nextNeedsAttendance = false) => {
      if (Date.now() < suppressRealtimeRefreshUntilRef.current) {
        return;
      }

      refreshNeedsAttendance = refreshNeedsAttendance || nextNeedsAttendance;

      if (refreshTimeout) {
        window.clearTimeout(refreshTimeout);
      }

      refreshTimeout = window.setTimeout(() => {
        const shouldRefreshAttendance = refreshNeedsAttendance;
        refreshNeedsAttendance = false;
        void loadState({
          preserveState: true,
          silent: true,
          refreshAttendance: shouldRefreshAttendance,
        });
      }, 650);
    };

    const channel = client
      .channel(`guild-live-state-${profile.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => scheduleRefresh())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profile_roles' }, () => scheduleRefresh())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profile_badges' }, () => scheduleRefresh())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'guild_badges' }, () => scheduleRefresh())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'signups' }, () => scheduleRefresh(true))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'event_attendance_records' }, () => scheduleRefresh())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'events' }, () => scheduleRefresh(true))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'event_slots' }, () => scheduleRefresh())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'event_parties' }, () => scheduleRefresh())
      .subscribe();

    return () => {
      if (refreshTimeout) {
        window.clearTimeout(refreshTimeout);
      }

      void client.removeChannel(channel);
    };
  }, [authMode, isApproved, loadState, needsOnboarding, profile]);

  const value = useMemo<GuildDataContextValue>(
    () => ({
      state,
      notice,
      isLoading,
      isRefreshing,
      error,
      currentMasses,
      dashboardSnapshot,
      bundledWeaponCatalogCount: weaponCatalogSeed.length,
      getEventDetailView: (eventId) => getEventDetail(state, eventId, profile?.id),
      getMySignups: () => (profile ? getSignupHistoryForProfile(state, profile.id) : []),
      joinSlot: async (_eventId, slotId) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in with Discord before joining a slot.' });
          return;
        }

        if (authMode === 'mock') {
          const { joinSlotForUser } = await import('@/features/signups/logic');
          const result = joinSlotForUser(state, profile, _eventId, slotId);
          setState(result.nextState);
          setNotice(result.notice);
          return;
        }

        const { data, error: rpcError } = await joinSlotRpc(slotId);

        if (rpcError) {
          setNotice({ kind: 'error', message: rpcError.message });
          return;
        }

        const status = data && typeof data === 'object' && !Array.isArray(data) ? data.status : undefined;
        setNotice({
          kind: status === 'waitlist' ? 'info' : 'success',
          message: status === 'waitlist' ? 'Added you to the waitlist.' : 'Joined the slot successfully.',
        });
        startManagedRefreshWindow();
        await loadState({ preserveState: true, refreshAttendance: true, attendanceEventId: _eventId });
      },
      cancelSignup: async (eventId) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in with Discord before changing signups.' });
          return;
        }

        if (authMode === 'mock') {
          const { cancelSignupForUser } = await import('@/features/signups/logic');
          const result = cancelSignupForUser(state, profile, eventId);
          setState(result.nextState);
          setNotice(result.notice);
          return;
        }

        const { error: rpcError } = await cancelSignupRpc(eventId);

        if (rpcError) {
          setNotice({ kind: 'error', message: rpcError.message });
          return;
        }

        setNotice({ kind: 'success', message: 'Removed your signup successfully.' });
        startManagedRefreshWindow();
        await loadState({ preserveState: true, refreshAttendance: true, attendanceEventId: eventId });
      },
      staffMoveSignup: async (signupId, targetEventSlotId) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before managing rosters.' });
          return;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'Roster management is disabled in mock mode.' });
          return;
        }

        const eventId = state.signups.find((signup) => signup.id === signupId)?.eventId;
        startManagedRefreshWindow();
        const { data, error: rpcError } = await staffMoveSignupRpc(signupId, targetEventSlotId);

        if (rpcError) {
          setNotice({ kind: 'error', message: rpcError.message });
          return;
        }

        const status =
          data && typeof data === 'object' && !Array.isArray(data) && data.status === 'waitlist'
            ? 'waitlist'
            : 'joined';

        setNotice({
          kind: 'success',
          message: status === 'waitlist' ? 'Moved the signup to the new slot waitlist.' : 'Moved the signup successfully.',
        });
        await loadState({ preserveState: true, refreshAttendance: true, attendanceEventId: eventId });
      },
      staffRemoveSignup: async (signupId) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before managing rosters.' });
          return;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'Roster management is disabled in mock mode.' });
          return;
        }

        const eventId = state.signups.find((signup) => signup.id === signupId)?.eventId;
        startManagedRefreshWindow();
        const { error: rpcError } = await staffRemoveSignupRpc(signupId);

        if (rpcError) {
          setNotice({ kind: 'error', message: rpcError.message });
          return;
        }

        setNotice({ kind: 'success', message: 'Removed the signup from the mass.' });
        await loadState({ preserveState: true, refreshAttendance: true, attendanceEventId: eventId });
      },
      saveProfileAccess: async (payload) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before managing guild members.' });
          return;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'User management is disabled in mock mode.' });
          return;
        }

        try {
          setIsRefreshing(true);
          startManagedRefreshWindow();
          await updateProfileAccess(payload);
          await Promise.all([loadState({ preserveState: true }), refreshProfile()]);
          setNotice({ kind: 'success', message: 'Updated guild member access successfully.' });
        } catch (nextError) {
          const message = nextError instanceof Error ? nextError.message : 'Failed to update guild member access.';
          setNotice({ kind: 'error', message });
          setIsRefreshing(false);
        }
      },
      setGuildMembershipActive: async (profileId, isActive) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before managing guild members.' });
          return;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'Guild membership controls are disabled in mock mode.' });
          return;
        }

        try {
          setIsRefreshing(true);
          startManagedRefreshWindow();
          await setGuildMembershipActiveRecord(profileId, isActive);
          await loadState({ preserveState: true });
          setNotice({
            kind: 'success',
            message: isActive ? 'Marked this member as active for guild Discord DMs.' : 'Marked this member as inactive for guild Discord DMs.',
          });
        } catch (nextError) {
          const message = nextError instanceof Error ? nextError.message : 'Failed to update guild membership status.';
          setNotice({ kind: 'error', message });
          setIsRefreshing(false);
        }
      },
      saveWeapon: async (payload) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before managing weapons.' });
          return;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'Weapon management is disabled in mock mode.' });
          return;
        }

        try {
          setIsRefreshing(true);
          startManagedRefreshWindow();
          await saveWeaponCatalogItem(payload);
          await loadState({ preserveState: true });
          setNotice({ kind: 'success', message: payload.id ? 'Weapon updated.' : 'Weapon added to the catalog.' });
        } catch (nextError) {
          const message = nextError instanceof Error ? nextError.message : 'Failed to save the weapon.';
          setNotice({ kind: 'error', message });
          setIsRefreshing(false);
        }
      },
      deleteWeapon: async (weaponId, confirmationText) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before managing weapons.' });
          return undefined;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'Weapon management is disabled in mock mode.' });
          return undefined;
        }

        try {
          setIsRefreshing(true);
          startManagedRefreshWindow();
          const result = await deleteWeaponCatalogItem(weaponId, confirmationText);
          await loadState({ preserveState: true });
          setNotice({
            kind: 'success',
            message:
              result.result === 'deleted'
                ? 'Weapon deleted permanently.'
                : 'Weapon is already referenced, so it was set inactive instead of being deleted.',
          });
          return result;
        } catch (nextError) {
          const message = nextError instanceof Error ? nextError.message : 'Failed to delete the weapon.';
          setNotice({ kind: 'error', message });
          setIsRefreshing(false);
          return undefined;
        }
      },
      syncWeaponCatalog: async () => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before syncing the weapon catalog.' });
          return;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'Weapon sync is disabled in mock mode.' });
          return;
        }

        try {
          setIsRefreshing(true);
          startManagedRefreshWindow();
          const summary = await syncBundledWeaponCatalog(state.weapons);
          await loadState({ preserveState: true });
          setNotice({
            kind: 'success',
            message: `Albion icon catalog synced: ${summary.inserted} added, ${summary.updated} updated, ${summary.unchanged} already current.`,
          });
        } catch (nextError) {
          const message = nextError instanceof Error ? nextError.message : 'Failed to sync the bundled weapon catalog.';
          setNotice({ kind: 'error', message });
          setIsRefreshing(false);
        }
      },
      saveEventBuilder: async (draft) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before managing events.' });
          return undefined;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'Event builder is disabled in mock mode.' });
          return undefined;
        }

        try {
          setIsRefreshing(true);
          startManagedRefreshWindow();
          const eventId = await saveEventBuilderDraft(draft, profile.id);
          await loadState({ preserveState: true });
          setNotice({ kind: 'success', message: draft.id ? 'Mass updated successfully.' : 'Mass created successfully.' });
          return eventId;
        } catch (nextError) {
          const message = nextError instanceof Error ? nextError.message : 'Failed to save the mass.';
          setNotice({ kind: 'error', message });
          setIsRefreshing(false);
          return undefined;
        }
      },
      saveTemplateBuilder: async (draft) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before managing templates.' });
          return undefined;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'Template management is disabled in mock mode.' });
          return undefined;
        }

        try {
          setIsRefreshing(true);
          startManagedRefreshWindow();
          const result = await saveTemplateBuilderDraft(draft, profile.id);
          await loadState({ preserveState: true });
          setNotice({ kind: 'success', message: draft.id ? 'Template updated successfully.' : 'Template created successfully.' });
          return result.templateId;
        } catch (nextError) {
          const message = nextError instanceof Error ? nextError.message : 'Failed to save the template.';
          setNotice({ kind: 'error', message });
          setIsRefreshing(false);
          return undefined;
        }
      },
      quickCreateEventFromTemplate: async (payload) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before managing events.' });
          return undefined;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'Template cloning is disabled in mock mode.' });
          return undefined;
        }

        try {
          setIsRefreshing(true);
          startManagedRefreshWindow();
          const eventId = await createEventFromTemplateQuick(payload);
          await loadState({ preserveState: true });
          setNotice({ kind: 'success', message: 'Created the mass from template.' });
          return eventId;
        } catch (nextError) {
          const message = nextError instanceof Error ? nextError.message : 'Failed to create the mass from the selected template.';
          setNotice({ kind: 'error', message });
          setIsRefreshing(false);
          return undefined;
        }
      },
      archiveEvent: async (eventId, force = false) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before archiving events.' });
          return;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'Archiving is disabled in mock mode.' });
          return;
        }

        const { error: rpcError } = await archiveEventRpc(eventId, force);

        if (rpcError) {
          setNotice({ kind: 'error', message: rpcError.message });
          return;
        }

        setNotice({ kind: 'success', message: 'Mass archived successfully.' });
        startManagedRefreshWindow();
        await loadState({ preserveState: true });
      },
      deleteEventPermanently: async (eventId, confirmationText) => {
        if (!profile) {
          setNotice({ kind: 'error', message: 'Sign in before deleting masses.' });
          return false;
        }

        if (authMode === 'mock') {
          setNotice({ kind: 'info', message: 'Mass deletion is disabled in mock mode.' });
          return false;
        }

        const { error: rpcError } = await deleteEventPermanentlyRpc(eventId, confirmationText);

        if (rpcError) {
          setNotice({ kind: 'error', message: rpcError.message });
          return false;
        }

        setNotice({ kind: 'success', message: 'Mass deleted permanently.' });
        startManagedRefreshWindow();
        await loadState({ preserveState: true });
        return true;
      },
      refreshData: async () => {
        await loadState({ preserveState: true });
      },
      clearNotice: () => setNotice(null),
    }),
    [
      authMode,
      currentMasses,
      dashboardSnapshot,
      error,
      isLoading,
      isRefreshing,
      loadState,
      notice,
      profile,
      refreshProfile,
      startManagedRefreshWindow,
      state,
    ],
  );

  return <GuildDataContext.Provider value={value}>{children}</GuildDataContext.Provider>;
}

export function useGuildData(): GuildDataContextValue {
  const context = useContext(GuildDataContext);

  if (!context) {
    throw new Error('useGuildData must be used within GuildDataProvider');
  }

  return context;
}
